/**
 * Where the Supabase session lives (mobile assessment §C auth row, §I).
 *
 * Web: localStorage, exactly as before — the client's own comment in
 * src/integrations/supabase/client.ts explains why sessionStorage was rejected.
 *
 * Native: the platform's secure store (Android Keystore-backed AES in
 * kanfy-mobile), through the M2 adapter in src/lib/nativeAuthStorage.ts. Before
 * auth-js reads anything it moves an M1 build's localStorage session into the
 * secure store, and ends a session whose persisted idle deadline passed while
 * the app was killed. It also keeps a wiped session unreachable when the
 * Keystore refuses a removal (brief §2.5).
 */
import type { SupportedStorage } from '@supabase/supabase-js';
import { createNativeAuthStorage } from '@/lib/nativeAuthStorage';
import { isEndedWebSession, sessionFromStoredAuthArtifact } from '@/lib/webSignOutFence';
import { withBrowserAuthMutation } from '@/lib/browserAuthMutationGate';
import { IS_NATIVE } from './index';

// The key is defined by the client module, which imports this one: read it at
// the adapter's boot, after every module has evaluated.
const authKey = async () => (await import('@/integrations/supabase/client')).SUPABASE_AUTH_KEY;

const WEB_AUTH_STORAGE_LOCK_PREFIX = 'kanfy-auth-storage-write:';

/** Shared with logout so session writes and destructive account-data cleanup exclude each other. */
export const webAuthStorageLockName = (key: string): string => `${WEB_AUTH_STORAGE_LOCK_PREFIX}${key}`;

async function mutateBrowserAuthStorage(key: string, mutation: () => void): Promise<void> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  if (!locks) {
    await withBrowserAuthMutation(mutation);
    return;
  }
  await locks.request(webAuthStorageLockName(key), { mode: 'exclusive' }, () => withBrowserAuthMutation(mutation));
}

// Auth-js does not take its own Web Lock for every session write (notably
// signInWithPassword). Put the exclusion at the storage boundary instead, so
// every current and future auth-js writer participates without each caller
// having to remember a separate lock.
interface BrowserAuthRemoval {
  expected: string | null;
  refused: boolean;
}

interface BrowserAuthCleanupSequence extends BrowserAuthRemoval {
  next: string;
  expectedVerifier: string | null;
  trailingVerifierRemoval: boolean;
}

interface BrowserAuthStorage extends SupportedStorage {
  consumeSessionRemoval(key: string): BrowserAuthRemoval | undefined;
}

export function createBrowserAuthStorage(): BrowserAuthStorage {
  // auth-js serializes session reads/refreshes within a client. Keep the
  // exact value that this tab used, independently of cross-tab auth events:
  // a failed S1 refresh must not remove S2, even after its broadcast arrives.
  // A session_id alone would also erase a newer refresh of the same session.
  const lastSeen = new Map<string, string | null>();
  const cleanup = new Map<string, BrowserAuthCleanupSequence>();
  const completedRemovals = new Map<string, BrowserAuthRemoval>();
  const sessionKeyFor = (key: string) => key.replace(/-(code-verifier|user)$/, '');

  return {
    consumeSessionRemoval(key) {
      const removal = completedRemovals.get(key);
      completedRemovals.delete(key);
      const sequence = cleanup.get(key);
      // Auth-js signOut calls _removeSession(), notifies SIGNED_OUT, then
      // removes the PKCE verifier a second time. Keep the exact S1 decision
      // through that trailing call so it cannot delete a replacement verifier.
      if (sequence) {
        sequence.next = `${key}-code-verifier`;
        sequence.trailingVerifierRemoval = true;
      }
      return removal;
    },
    getItem(key) {
      const sessionKey = sessionKeyFor(key);
      if (cleanup.get(sessionKey)?.next !== '') cleanup.delete(sessionKey);
      completedRemovals.delete(sessionKey);
      const value = localStorage.getItem(key);
      if (key === sessionKey) lastSeen.set(key, value);
      return value;
    },
    setItem(key, value) {
      const sessionKey = sessionKeyFor(key);
      if (cleanup.get(sessionKey)?.next !== '') cleanup.delete(sessionKey);
      completedRemovals.delete(sessionKey);
      return mutateBrowserAuthStorage(sessionKey, () => {
        // auth-js saves a refreshed session BEFORE notifying listeners. Reject
        // ended sessions here, while holding the write lock, so an old refresh
        // cannot overwrite a newer login before its auth event reaches this tab.
        const session = sessionFromStoredAuthArtifact(value);
        if (session && isEndedWebSession(session)) return;
        localStorage.setItem(key, value);
        if (key === sessionKey) lastSeen.set(key, value);
      });
    },
    removeItem(key) {
      const sessionKey = sessionKeyFor(key);
      if (key === sessionKey) {
        // Capture before waiting for the lock: another tab can replace the
        // value while this removal is queued. With no prior read/write, take
        // a snapshot now so ordinary removal remains supported.
        const expected = lastSeen.has(key) ? lastSeen.get(key)! : localStorage.getItem(key);
        lastSeen.set(key, expected);
        cleanup.delete(key);
        completedRemovals.delete(key);
        return mutateBrowserAuthStorage(key, () => {
          const current = localStorage.getItem(key);
          const refused = current !== null && current !== expected;
          cleanup.set(key, {
            expected,
            refused,
            next: `${key}-code-verifier`,
            expectedVerifier: localStorage.getItem(`${key}-code-verifier`),
            trailingVerifierRemoval: false,
          });
          if (refused) return;
          localStorage.removeItem(key);
          // Keep the operation's read/write identity: startup recovery can
          // call _removeSession twice for the same failed refresh.
        });
      }

      // _removeSession removes main -> verifier -> user with no intervening
      // reads/writes. Carry its decision through exactly that sequence, then
      // release it so independent PKCE/verifier cleanup still works normally.
      const sequence = cleanup.get(sessionKey);
      if (sequence?.next === key) {
        if (sequence.trailingVerifierRemoval) cleanup.delete(sessionKey);
        else if (key.endsWith('-code-verifier')) sequence.next = `${sessionKey}-user`;
        else sequence.next = '';
        return mutateBrowserAuthStorage(sessionKey, () => {
          const current = localStorage.getItem(sessionKey);
          const sessionStillMatches = current === null || current === sequence.expected;
          const verifierStillMatches = !key.endsWith('-code-verifier') ||
            localStorage.getItem(key) === sequence.expectedVerifier;
          if (!sequence.refused && sessionStillMatches && verifierStillMatches) localStorage.removeItem(key);
          // Publish only at the end of auth-js's cleanup sequence, immediately
          // before it emits SIGNED_OUT. Its listener must use the operation's
          // S1 snapshot, not a state.session already changed by S2's broadcast.
          if (key.endsWith('-user')) {
            completedRemovals.set(sessionKey, sequence);
            sequence.next = '';
          }
        });
      }
      cleanup.delete(sessionKey);
      return mutateBrowserAuthStorage(sessionKey, () => localStorage.removeItem(key));
    },
  };
}

export const browserAuthStorage = createBrowserAuthStorage();

export const consumeBrowserAuthRemoval = (key: string) => browserAuthStorage.consumeSessionRemoval(key);

export const authStorage: SupportedStorage = IS_NATIVE ? createNativeAuthStorage(authKey) : browserAuthStorage;
